/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { getLocalDateInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  DATE_AGGREGATION,
  MI_DEFAULT_DATE_FORMAT,
  REPORT_IDS,
} from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  CALENDAR_FILTERS,
  CALENDAR_FILTERS_PREFIX,
  DEFAULT_DAYS_SHOWN_IN_OVERVIEW,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
  FIXED_EXTRACT_DATE_LATEST,
  OTB_TABS,
  SEARCHES_TABS,
} from 'lumen/utils/constants/market-insight-constants';
import dateRangeDisplay from 'lumen/utils/dates/date-range-display';
import {
  parseDateRangeString,
  parseObject,
  stringifyObject,
} from 'lumen/utils/parse-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';
import PerfectScrollbar from 'perfect-scrollbar';

// Scrolltop threshold for the main sidebar. At this point we will hide/show the Opportunities to discover message
const MAIN_SIDEBAR_OPPORTUNITIES_TO_DISCOVER_SCROLL_THRESHOLD = 255;

// Above this threshold we show the overview chart by week instead of by day
const DATE_AGGREGATION_THRESHOLD = 60;

export default class HotelMarketInsightController extends Controller {
  queryParams = [
    'selectedCalendarViewFilterIds',
    'activeFilters',
    'extractDateIndex',
    'showAllEvents',
    'evolutionType',
    'focusedDetailSection',
    'fromDate',
    'toDate',
    'dateAggregation',
    'otbTab',
    'searchesTab',
    'searchesDetailsItem',
    'scheduleReport',
    'isSearchesCardExpanded',
    'refDaysAgo',
    'scrollTo',
  ];

  @service access;
  @service ajax;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service miRateindex;
  @service miSearches;
  @service miSelections;
  @service store;
  @service usageAnalytics;

  @tracked activeFilters = '';
  @tracked dateAggregation = DATE_AGGREGATION.DAY;
  @tracked extractDateIndex =
    DEFAULT_QP_VALUES.hotel['market-insight'].extractDateIndex;
  // The Focused detail section is the id of the card that takes over the whole detail pane
  // There can only be one focused card at a time
  @tracked focusedDetailSection =
    DEFAULT_QP_VALUES.hotel['market-insight'].focusedDetailSection;
  @tracked fromDate = null;
  // In the blue bar is a dropdown where users can select a reference day (e.g. vs 3 days ago)
  @tracked hasInitialisedMiSelections = false;
  // Silly flag to hide / show the "X more opportunities to discover" message in the main sidebar
  @tracked hasReachedMainSidebarScrollThreshold = false;
  @tracked isNavbarHovered = false;
  @tracked isSearchesCardExpanded =
    DEFAULT_QP_VALUES.hotel['market-insight'].isSearchesCardExpanded;
  @tracked isShowingMIRatePreferenceModal = false;
  // The following mouseOver properties are needed because we need to enable marker toggling in the map component from within other components.
  @tracked mouseOutMarkerId = null;
  // These properties store the hovered ids. The city map will check these with a did-update
  @tracked mouseOverMarkerId = null;
  @tracked otbTab = OTB_TABS.MY_OTB;

  // This value will be used throughout the MI page to visualise data vs. this reference
  @tracked refDaysAgo = DEFAULT_QP_VALUES.hotel['market-insight'].refDaysAgo;
  @tracked scheduleReport = false;
  // Variable that will contain the window.innerHeight (used for the X opportunities to discover message in the main sidebar)
  // This value is set once on the did-insert (for the intial value) & is updated afterwards by the window resize handler
  @tracked screenHeight = 0;
  @tracked scrollTo = DEFAULT_QP_VALUES.hotel['market-insight'].scrollTo;
  @tracked searchesDetailsItem =
    DEFAULT_QP_VALUES.hotel['market-insight'].searchesDetailsItem;
  @tracked searchesTab = DEFAULT_QP_VALUES.hotel['market-insight'].searchesTab;

  @tracked selectedCalendarViewFilterIds =
    DEFAULT_QP_VALUES.hotel['market-insight'].selectedCalendarViewFilterIds;
  // This will be false until the first time we go to a daily view
  @tracked shouldRenderMap = false;
  @tracked shouldWaitToRenderEventMarkers = false;

  @tracked showAcceptedEvents = true;
  @tracked showAllEvents =
    DEFAULT_QP_VALUES.hotel['market-insight'].showAllEvents;
  @tracked showOwnHotel = true;
  // Note: The tracked flags below handle the visibility status of different markers on the map (all are true by default)
  @tracked showPrimaryCompset = true;
  @tracked showSmartCompset = true;
  @tracked showSoldouts = true;
  @tracked toDate = null;

  // Secret URL parameter (not used in the UI - but mainly for debugging/research purposes)
  evolutionType = DEFAULT_QP_VALUES.hotel['market-insight'].evolutionType;
  exportChoices = [
    { id: REPORT_IDS.EXCEL, toTranslate: 'general.export' },
    { id: REPORT_IDS.SCHEDULED, toTranslate: 'reports.schedule_report' },
  ];

  // Flag to be sure we only add the window resize handler once!
  hasAddedOnWindowResizeEventHandler = false;
  mainSidebarWrapperId = `mi-main-sidebar-${guidFor(this)}`;
  perfectScrollbarMainSidebarInstance = null;
  privateMarketInsightWindowResizeHandler = null;

  onWindowResizeHandlerTask = task({ keepLatest: true }, async () => {
    // Small timeout to make page a little more responsive
    await timeout('extra-short');

    if (this.perfectScrollbarMainSidebarInstance) {
      this.perfectScrollbarMainSidebarInstance.update();
    }

    if (this.perfectScrollbarDetailPaneInstance) {
      this.perfectScrollbarDetailPaneInstance.update();
    }

    this.screenHeight = window.innerHeight;
  });

  @computed(
    'model.map.data.geoData.meta.extract_dates',
    'isDetailPaneLoading',
    'extractDateIndex',
  )
  get currentExtractDate() {
    if (
      !this.isDetailPaneLoading &&
      this.model.map.data.geoData?.meta?.extract_dates
    ) {
      const currentExtractDate =
        this.model.map.data.geoData.meta.extract_dates[this.extractDateIndex];

      if (currentExtractDate === FIXED_EXTRACT_DATE_LATEST) {
        return currentExtractDate;
      }

      return dayjs(currentExtractDate);
    }

    return null;
  }

  @computed(
    'model.map.data.geoData.meta.extract_dates',
    'isDetailPaneLoading',
    'extractDateIndex',
  )
  get currentExtractDateUTC() {
    if (
      !this.isDetailPaneLoading &&
      this.model.map.data.geoData?.meta?.extract_dates
    ) {
      const currentExtractDate =
        this.model.map.data.geoData.meta.extract_dates[this.extractDateIndex];

      if (currentExtractDate === FIXED_EXTRACT_DATE_LATEST) {
        return currentExtractDate;
      }

      return dayjs.utc(currentExtractDate);
    }

    return null;
  }

  get datePickerRange() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  get datePickerRangeOptions() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();

    return [
      {
        title: this.intl.t('revenue_insight.report_selected_period').toString(),
        options: [
          {
            text: 'general.next_days',
            count: 30,
            id: 'thirty_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(30, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 60,
            id: 'sixty_days',
            isSelected: true,
            range: {
              start: todayMoment,
              end: todayMoment.add(60, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 90,
            id: 'ninety_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(90, 'day'),
            },
          },
          {
            text: 'general.custom',
            id: 'custom',
            isSelected: false,
            range: {
              start: null,
              end: null,
            },
          },
        ],
      },
    ];
  }

  get favourites() {
    return Array.from(this.hotelAndMarketService.hotel.favouriteFilters);
  }

  get fromDateMoment() {
    return this.hotelAndMarketService.toLocalTimeWithFallback(this.fromDate);
  }

  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  @computed('model.map.isLoading')
  get isCityMapLoading() {
    return this.model?.map?.isLoading;
  }

  @computed('model.detail.isLoading')
  get isDetailPaneLoading() {
    return this.model?.detail?.isLoading;
  }

  get isLoadingGlobal() {
    return (
      this.isSidebarCalendarLoading ||
      this.isCityMapLoading ||
      this.isDetailPaneLoading ||
      this.isOverviewLoading
    );
  }

  get isMarketInsightFull() {
    return this.access.hasFeature('app.market_insight.full');
  }

  @computed('model.overview.isLoading')
  get isOverviewLoading() {
    return this.model?.overview?.isLoading;
  }

  @computed('model.calendar.isLoading')
  get isSidebarCalendarLoading() {
    return this.model?.calendar?.isLoading;
  }

  // Transform the active filters object to the format FilterBar expects
  get mappedActiveFilters() {
    const mappedActiveFilters = [];
    Object.keys(this.parsedActiveFilters).forEach((filterKey) => {
      const filterValue = this.parsedActiveFilters[filterKey];
      mappedActiveFilters.push({
        key: filterKey,
        translationKey: `market_insight.filters.${filterKey}`,
        values: [
          {
            key: filterValue,
            displayValue: HotelMarketInsightController.filterValueFormatter(
              filterKey,
              filterValue,
              this.intl,
            ),
          },
        ],
      });
    });

    // Make sure the range filter is always displayed first
    mappedActiveFilters.sort((a, b) => {
      if (a.key === ACTIVE_FILTERS.RANGE) {
        return -1;
      }
      if (b.key === ACTIVE_FILTERS.RANGE) {
        return 1;
      }
      return 0;
    });

    return mappedActiveFilters;
  }

  get miSettingsOptions() {
    if (this.isMarketInsightFull) {
      return [
        {
          id: 'change-rate-preference',
          label: this.intl.t('market_insight.settings.change_rate_preference'),
        },
        {
          id: 'compset-link',
          label: this.intl.t('market_insight.settings.go_to_compset_settings'),
          unsafe_isLink: true,
        },
      ];
    }

    return [
      {
        id: 'compset-link',
        label: this.intl.t('market_insight.settings.go_to_compset_settings'),
        unsafe_isLink: true,
      },
    ];
  }

  get miSettingsValue() {
    return {
      icon: 'settings',
    };
  }

  @computed(
    'isDetailPaneLoading',
    'model.map.data.geoData.meta.extract_dates.length',
  )
  get nbOfAvailableExtractDates() {
    if (
      !this.isDetailPaneLoading &&
      this.model.map.data.geoData?.meta?.extract_dates
    ) {
      return this.model.map.data.geoData.meta.extract_dates.length;
    }

    return null;
  }

  get overviewRange() {
    // Return the range filter if one exists
    if (this.selectedRange) {
      return {
        start: this.hotelAndMarketService.toLocalTime(this.selectedRange.start),
        end: this.hotelAndMarketService.toLocalTime(this.selectedRange.end),
      };
    }

    // Return the same range as the DatePicker by default when there are no filters
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  get parsedActiveFilters() {
    if (this.activeFilters) {
      return parseObject(this.activeFilters);
    }
    return {};
  }

  // CP that selects all rateindex objects (for own hotel & all competitors) for the current day & extract date
  get rateIndexDataForCurrentSelection() {
    if (!this.isDetailPaneLoading && this.selectedDayModel) {
      return this.miRateindex.getRateIndexesForExtractDate(
        this.selectedDayModel,
        this.currentExtractDateUTC,
      );
    }

    return null;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get refDaysAgoSelectionOptions() {
    const numDaysAgos = [1, 3, 7, 14];

    return numDaysAgos.map((numDaysAgo) => {
      return {
        id: numDaysAgo,
        label: this.intl.t('general.daysago', { count: numDaysAgo }),
      };
    });
  }

  @dependentKeyCompat
  get selectedDay() {
    return this.parsedActiveFilters[ACTIVE_FILTERS.DAY] || null;
  }

  // Add the hotel id in the watched properties (avoid issue when you switch hotels when you haven't updated the standard selectedDay - this needs to trigger again)
  // Note: I've added 'model.{calendar.isLoading,detail.isLoading}' in the watched properties
  // The detail pane currently checks this CP if it should be displayed or not. The content in the panel is dependant on the summaries & detail request content.
  // We can't add those properties in the template check, because we don't want the detail pane to be hidden-displayed again when changing the selected day (causes detail.isLoading to switch to true)
  // Adding the properties here is a workaround to make sure the detail pane is correct on initial page load
  @computed(
    'selectedDay',
    'hotelAndMarketService.hotel.id',
    'isSidebarCalendarLoading',
    'isDetailPaneLoading',
  )
  get selectedDayModel() {
    if (this.selectedDay) {
      return this.store.peekRecord('day', this.selectedDay);
    }

    return null;
  }

  // selectedDay as a dayjs object. This is used by Ember Power Calendar day component to see if the day is selected or not
  // Note: according to docs this should work without the @computed, but it doesn't fire otherwise
  get selectedDayMoment() {
    return dayjs(this.selectedDay);
  }

  @dependentKeyCompat
  get selectedRange() {
    const rangeFilter = this.parsedActiveFilters[ACTIVE_FILTERS.RANGE];
    if (rangeFilter) {
      return parseDateRangeString(rangeFilter);
    }
    return null;
  }

  get selectedRefDaysAgo() {
    return this.refDaysAgoSelectionOptions.find(
      (refDaysAgoOption) => refDaysAgoOption.id === this.refDaysAgo,
    );
  }

  get sidebarCalendarRange() {
    // If there is a day filter: return a range with the day as both start and end of range
    if (this.selectedDay) {
      const selectedDayDate = this.hotelAndMarketService.toLocalTime(
        this.selectedDay,
      );
      return {
        start: selectedDayDate,
        end: selectedDayDate,
      };
    }

    // If there is a range filter: return the range with Date objects
    if (this.selectedRange) {
      return {
        start: this.hotelAndMarketService.toLocalTime(this.selectedRange.start),
        end: this.hotelAndMarketService.toLocalTime(this.selectedRange.end),
      };
    }

    // Return the same range as the DatePicker by default when there are no filters
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  @dependentKeyCompat
  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  get toDateMoment() {
    if (this.toDate) {
      return this.hotelAndMarketService.toLocalTime(this.toDate);
    }
    return this.hotelAndMarketService
      .getLocalTime()
      .add(DEFAULT_DAYS_SHOWN_IN_OVERVIEW, 'day');
  }

  willDestroy() {
    window.removeEventListener(
      'resize',
      this.privateMarketInsightWindowResizeHandler,
    );
  }

  static filterValueFormatter(filterKey, filterValue, intl) {
    switch (filterKey) {
      case ACTIVE_FILTERS.DAY: {
        return dayjs(filterValue).format(MI_DEFAULT_DATE_FORMAT);
      }
      case ACTIVE_FILTERS.RANGE: {
        const { start, end } = parseDateRangeString(filterValue);
        return dateRangeDisplay(dayjs(start), dayjs(end), intl);
      }
      default: {
        return filterValue;
      }
    }
  }

  @action
  checkDateAggregation() {
    const dayDiff = this.overviewRange.end.diff(
      this.overviewRange.start,
      'day',
    );
    // Update the default date aggregation to 'week' for ranges longer than the threshold (60 days) and to 'day' for ranges shorted than the threshold
    const defaultDateAggregation =
      dayDiff > DATE_AGGREGATION_THRESHOLD
        ? DATE_AGGREGATION.WEEK
        : DATE_AGGREGATION.DAY;
    this.setDateAggregation(defaultDateAggregation);
  }

  @action
  clearActiveFilter(filterId, filterValue) {
    const currentFilters = this.parsedActiveFilters;
    if (currentFilters[filterId] === filterValue) {
      delete currentFilters[filterId];
    }
    // When clearing the range filter, also clear the day filter (if there is one)
    if (
      filterId === ACTIVE_FILTERS.RANGE &&
      currentFilters[ACTIVE_FILTERS.DAY]
    ) {
      delete currentFilters[ACTIVE_FILTERS.DAY];
    }
    this.activeFilters = stringifyObject(currentFilters);

    this.checkDateAggregation();

    // Clear out focusedDetailSection QP
    this.setFocusedDetailSection('');

    // Clear out searchesDetailsItem QP
    this.updateSearchesDetailsItem(
      DEFAULT_QP_VALUES.hotel['market-insight'].searchesDetailsItem,
    );
  }

  @action
  closeRatePreferenceSettingsModal() {
    this.isShowingMIRatePreferenceModal = false;
  }

  @action
  closeScheduleModal() {
    this.scheduleReport = false;
  }

  @action
  doRefresh() {
    this.send('refreshRoute');
  }

  @action
  excelExport() {
    const url = formatUrl(
      '/apigateway/v1/app/download/marketinsight',
      {
        fromDate: this.overviewRange.start.format('YYYY-MM-DD'),
        toDate: this.overviewRange.end.format('YYYY-MM-DD'),
        date_aggregation: this.dateAggregation,
        hotel_id: this.hotelAndMarketService.hotel.id,
      },
      this.ajax.host,
    );

    openUrl(url);
  }

  @action
  handleExport(exportChoice) {
    if (exportChoice.id === REPORT_IDS.SCHEDULED) {
      this.scheduleReport = true;
    } else {
      this.excelExport();

      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'filter-bar',
      });
    }
  }

  @action initMiSelections() {
    // Only show future dates for now!
    this.miSelections.minAllowedDay = getLocalDateInBrowserTimezone(
      this.hotelAndMarketService.timezone,
    );
    this.miSelections.showAllEvents = Boolean(this.showAllEvents);
    this.miSelections.dateAggregation = this.dateAggregation;
    this.miSelections.otbTab = this.otbTab;
    this.miSelections.searchesTab = this.searchesTab;
    this.miSelections.searchesDetailsItem = this.searchesDetailsItem;

    this.hasInitialisedMiSelections = true;
  }

  @action
  initPerfectScrollbarMainSidebar() {
    const mainSidebarDomElement = document.querySelector(
      `#${this.mainSidebarWrapperId}`,
    );

    if (mainSidebarDomElement) {
      this.perfectScrollbarMainSidebarInstance = new PerfectScrollbar(
        mainSidebarDomElement,
        {
          wheelPropagation: false,
        },
      );

      mainSidebarDomElement.addEventListener('ps-scroll-y', () => {
        const scrollTop = mainSidebarDomElement.scrollTop;

        if (
          scrollTop < MAIN_SIDEBAR_OPPORTUNITIES_TO_DISCOVER_SCROLL_THRESHOLD
        ) {
          this.hasReachedMainSidebarScrollThreshold = false;
        } else {
          this.hasReachedMainSidebarScrollThreshold = true;
        }
      });
    }
  }

  @action
  onClearFilterSelection() {
    this.resetSearchesDetailsItem();
    this.setFocusedDetailSection('');
  }

  @action
  onDidInsertMarketInsightElements() {
    this.miSelections.selectedRefDaysAgo = this.selectedRefDaysAgo;

    // Make sure we only add the window resize event handler once
    // We do it like this because we can't use the controller constructor (only happens once & we remove the handler on page switch)
    if (!this.hasAddedOnWindowResizeEventHandler && this.isMarketInsightFull) {
      // Attach namespaced event handler so we are able to detect window resizes
      // + Note: initial execution on page load is handled by the {{did-insert}} render modifier on the calendar-section-main-content element
      this.privateMarketInsightWindowResizeHandler =
        this.onWindowResize.bind(this);
      window.addEventListener(
        'resize',
        this.privateMarketInsightWindowResizeHandler,
      );

      this.hasAddedOnWindowResizeEventHandler = true;

      // Set initial screen height value on did-insert
      this.screenHeight = window.innerHeight;
    }
  }

  @action
  onNavbarHoverChange(isHovered) {
    this.isNavbarHovered = isHovered;
  }

  @action
  onRefDaysAgoSelectionChanged(selectedReference) {
    this.refDaysAgo = selectedReference.id;

    // Also update in selections service
    this.miSelections.selectedRefDaysAgo = selectedReference;
  }

  @action
  onSettingsSelect(selectedOption) {
    if (selectedOption.id === 'change-rate-preference') {
      this.isShowingMIRatePreferenceModal = true;
    }

    if (selectedOption.id === 'compset-link') {
      openUrl(
        `${this.ajax.host}/hotel/${this.hotelAndMarketService.hotel?.id}/settings/competitors`,
      );
    }
  }

  @action
  onWindowResize() {
    this.onWindowResizeHandlerTask.perform();
  }

  @action
  rangeChanged(range) {
    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }
    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }

    this.activeFilters = '';

    this.checkDateAggregation();
  }

  @action
  removeScrollTo() {
    this.scrollTo = null;
  }

  @action
  resetSearchesDetailsItem() {
    this.updateSearchesDetailsItem(
      DEFAULT_QP_VALUES.hotel['market-insight'].searchesDetailsItem,
    );
  }

  @action
  setActiveFilters(filterId, filterValue) {
    if (filterId === DATE_AGGREGATION.DAY) {
      this.shouldRenderMap = true;
    }

    // Update the current filters
    const currentFilters = this.parsedActiveFilters;
    currentFilters[filterId] = filterValue;

    this.activeFilters = stringifyObject(currentFilters);

    this.checkDateAggregation();
  }

  @action
  setDateAggregation(dateAggregation) {
    this.dateAggregation = dateAggregation;
    this.miSelections.dateAggregation = this.dateAggregation;
  }

  @action
  setFocusedDetailSection(cardId) {
    this.focusedDetailSection = cardId;

    if (cardId === DETAIL_PANE_DETAIL_SECTION_CARD_IDS.COMPETITORS_LIST) {
      this.usageAnalytics.track('Smart Compset Composition Viewed', {
        eventTrigger: 'market-insight-map-legend',
      });
    }
  }

  // This action allows us to update the mouseOverMarkerId & mouseOutMarkerId values from within other components.
  // These properties are passed to the city map & observed through an did-update. On update the city map component will try to open/close a marker tooltip (by faking the events)
  @action
  setHighlightedMarker(markerId, isHovered) {
    if (isHovered) {
      this.mouseOverMarkerId = markerId;
      this.mouseOutMarkerId = null;
    } else {
      this.mouseOutMarkerId = markerId;
      this.mouseOverMarkerId = null;
    }
  }

  @action
  setSelectedCalendarViewFilterIds(filter) {
    const selectedCalenderViewFilterIds = [
      ...this.selectedCalendarViewFilterIds,
    ];

    // New filter we want to add is a price filter
    if (filter.id.includes(CALENDAR_FILTERS_PREFIX.PRICE)) {
      // Check if there's already a price filter active at the moment
      const isPriceFilterActive =
        selectedCalenderViewFilterIds.filter((calendarFilter) =>
          calendarFilter.includes(CALENDAR_FILTERS_PREFIX.PRICE),
        ).length > 0;

      if (isPriceFilterActive) {
        const priceFilterIndex = selectedCalenderViewFilterIds.findIndex(
          (calendarFilter) =>
            calendarFilter.includes(CALENDAR_FILTERS_PREFIX.PRICE),
        );
        selectedCalenderViewFilterIds.splice(priceFilterIndex, 1);
      }

      // Only push the new filter in case it's not the 'Any ...' (which is the same as no filter)
      if (filter.id !== CALENDAR_FILTERS.PRICE_ANY) {
        selectedCalenderViewFilterIds.push(filter.id);
      }
    }

    // New filter we want to add is a demand filter
    if (filter.id.includes(CALENDAR_FILTERS_PREFIX.DEMAND)) {
      // Check if there's already a demand filter active at the moment
      const isDemandFilterActive =
        selectedCalenderViewFilterIds.filter((calendarFilter) =>
          calendarFilter.includes(CALENDAR_FILTERS_PREFIX.DEMAND),
        ).length > 0;

      if (isDemandFilterActive) {
        const demandChangeFilterIndex = selectedCalenderViewFilterIds.findIndex(
          (calendarFilter) =>
            calendarFilter.includes(CALENDAR_FILTERS_PREFIX.DEMAND),
        );
        selectedCalenderViewFilterIds.splice(demandChangeFilterIndex, 1);
      }

      // Only push the new filter in case it's not the 'Any ...' (which is the same as no filter)
      if (filter.id !== CALENDAR_FILTERS.DEMAND_ANY) {
        selectedCalenderViewFilterIds.push(filter.id);
      }
    }

    this.selectedCalendarViewFilterIds = selectedCalenderViewFilterIds;
  }

  @action
  toggleMapVisibilityFlag(visibilityFlagToToggle) {
    this[visibilityFlagToToggle] = !this[visibilityFlagToToggle];
  }

  @action
  toggleSearchesCard() {
    this.isSearchesCardExpanded = !this.isSearchesCardExpanded;

    if (this.isSearchesCardExpanded) {
      this.usageAnalytics.track('Search Traffic Detail Viewed', {
        eventTrigger: 'search-traffic-modal',
      });
    }
  }

  @action
  toggleShowAllEvents() {
    this.showAllEvents = !this.showAllEvents;
    this.miSelections.showAllEvents = this.showAllEvents;
  }

  // Passed to filter bar component to be able to set favourites & clear the selection
  @action
  updateActiveFiltersToParameter(updatedFilterValues) {
    this.activeFilters = updatedFilterValues.segmentationFilters;

    // Reset the extract date index because the activeFilters might update the selected date
    this.extractDateIndex = 0;

    this.checkDateAggregation();
  }

  @action
  updateExtractDateIndex(newIndex) {
    this.extractDateIndex = newIndex;
  }

  @action
  updateMapRender() {
    this.shouldRenderMap = Boolean(this.selectedDay);
  }

  @action
  updateOTBTab(tabId) {
    this.otbTab = tabId;
    this.miSelections.otbTab = this.otbTab;
  }

  @action
  updateSearchesDetailsItem(searchType) {
    this.searchesDetailsItem = searchType;
    this.miSelections.searchesDetailsItem = this.searchesDetailsItem;
  }

  @action
  updateSearchesTab(tabId) {
    this.searchesTab = tabId;
    this.miSelections.searchesTab = this.searchesTab;
  }

  @action
  updateSelectedDay(newDate) {
    const dateToCheck = newDate.moment;

    // Extra checks to see if we have enough data to display something useful for this day
    // -> if the market is missing, the map will look like crap
    const dataDateToCheck = newDate.day;
    const hasData =
      dataDateToCheck &&
      dataDateToCheck.mostRecentDailySummary &&
      !dataDateToCheck.mostRecentDailySummary.isDestinationDemandIndexMissing;

    if (
      hasData &&
      dateToCheck.isSameOrAfter(this.miSelections.minAllowedDay, 'day') &&
      dateToCheck.isSameOrBefore(this.miSelections.maxAllowedDay, 'day')
    ) {
      this.shouldWaitToRenderEventMarkers = true;
      this.setActiveFilters(ACTIVE_FILTERS.DAY, newDate.id);

      // Reset the extract date index because the selected day changed
      this.extractDateIndex = 0;
    }
  }

  @action
  validateSearchesTab() {
    // Validate the searchesTab QP: the supported searches are only available when the gridRequest has been fetched in the model (see setSupportedSearchTypesForCurrentDestination)
    const {
      areFlightLeisureSearchesSupportedForTheCurrentDestination,
      areFlightBusinessSearchesSupportedForTheCurrentDestination,
      areHotelLeisureSearchesSupportedForTheCurrentDestination,
      areHotelBusinessSearchesSupportedForTheCurrentDestination,
    } = this.miSearches;

    const searchTabParametersValidity = {
      [SEARCHES_TABS.FLIGHTS]:
        areFlightLeisureSearchesSupportedForTheCurrentDestination,
      [SEARCHES_TABS.FLIGHTSGDS]:
        areFlightBusinessSearchesSupportedForTheCurrentDestination,
      [SEARCHES_TABS.HOTELS]:
        areHotelLeisureSearchesSupportedForTheCurrentDestination,
      [SEARCHES_TABS.HOTELSGDS]:
        areHotelBusinessSearchesSupportedForTheCurrentDestination,
    };

    if (this.searchesTab && !searchTabParametersValidity[this.searchesTab]) {
      // incorrect searchesTab value is selected. Set a new valid one
      const firstSupportedSearchesType = Object.entries(
        searchTabParametersValidity,
      ).find(([, isSupported]) => isSupported);

      if (firstSupportedSearchesType) {
        this.searchesTab = firstSupportedSearchesType[0];
        this.miSelections.searchesTab = this.searchesTab;
      } else {
        this.searchesTab =
          DEFAULT_QP_VALUES.hotel['market-insight'].searchesTab;
        this.miSelections.searchesTab = this.searchesTab;
      }
    }
  }
}
